import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { cache } from '@sama/rxjs-core';
import { flattenDeep, size, some } from 'lodash';
import { combineLatest, EMPTY, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { ObjectSelectionService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/object-selection.service';
import { SelectionService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/selection.service';
import { ObjectScopingService } from '../../../../../../../core/shared/misc-services/object-scoping.service';
import {
  ObjectState,
  ObjectTransientInfoState,
} from '../../../../../../../cross-team-shared/annotation/shared/state/object-state';
import { BrowserControlService } from '../../../../../../../cross-team-shared/misc-services/browser-control.service';
import {
  assertStore,
  isStoreDefined,
} from '../../../../../../../cross-team-shared/misc-utils';
import { combineLatestOrEmpty } from '../../../../../../../cross-team-shared/operators/combine-latest-or-empty';
import { AnnotationListService } from '../../annotation-list/annotation-list.service';
import { ObjectDragAndDropService } from '../../annotation-list/object-drag-and-drop.service';

@Component({
  selector: 'h3-object-list-item',
  templateUrl: './object-list-item.component.html',
  styleUrls: ['./object-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ObjectListItemComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input({ required: true }) objectItem!: ObjectState;
  @ViewChild('scopeObjectButton', { read: ElementRef })
  private scopeObjectButton?: ElementRef;
  selected$!: Observable<boolean | undefined>;
  isNestingTarget$!: Observable<boolean>;
  color$!: Observable<string>;
  shapeCount$!: Observable<number>;
  subObjectCount$!: Observable<number>;
  missingAnnotationsTooltip$!: Observable<string>;

  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private annotationListService = inject(AnnotationListService);
  private selectionService = inject(SelectionService);
  private browserControlService = inject(BrowserControlService);
  private dragAndDropService = inject(ObjectDragAndDropService);
  private objectScopingService = inject(ObjectScopingService);
  private objectSelectionService = inject(ObjectSelectionService);
  private objectTransientInfoStore!: Store<ObjectTransientInfoState>;
  private objectStore!: Store<ObjectState>;
  private elementRef = inject(ElementRef);

  @HostListener('click', ['$event'])
  selectObject(event: MouseEvent) {
    if (event.target === this.scopeObjectButton?.nativeElement) {
      return;
    }
    const eventContext = { source: 'object_list' };
    const itemIndex = this.objectItem.index;
    const lastSelection = this.objectSelectionService.getLastSelectedObjectId();

    if (event[this.browserControlService.getPlatformModifier()]) {
      this.selectionService.toggleObjectSelection(itemIndex, eventContext);
    } else if (event.shiftKey && isDefined(lastSelection)) {
      const newSelection = this.annotationListService.getObjectsBetween(
        this.taskWorkspaceStore.getObjectStore(lastSelection).state(),
        this.objectItem,
      );
      this.selectionService.setSelection(newSelection, [], eventContext);
    } else {
      this.selectionService.setSelection([itemIndex], [], eventContext);
    }
  }

  @HostListener('dblclick', ['$event'])
  scopeObject(_event: MouseEvent, source = 'mouse_double') {
    const { index } = this.objectItem;
    this.objectScopingService.scopeObject(index, source, 'scope');
    this.selectionService.selectFirstItemInObject(this.objectItem.index);
  }

  ngOnInit() {
    const index = this.objectItem.index;
    this.objectTransientInfoStore = this.getObjectTransientInfoStore(index);

    this.objectStore = this.taskWorkspaceStore.getObjectStore(index);

    this.selected$ = this.taskWorkspaceStore('objectSelection')(index).$;

    this.isNestingTarget$ = this.dragAndDropService(
      'targetNestingObjectId',
    ).$.pipe(map((targetId) => targetId === this.objectItem.index));

    this.color$ = this.getColor$();
    this.subObjectCount$ = this.getSubObjectCount$();
    this.shapeCount$ = this.getShapeCount$();
    this.missingAnnotationsTooltip$ = this.getMissingAttributesTooltip$();
    this.setUpScrollIntoViewSubscription();
  }

  private getColor$() {
    return this.objectTransientInfoStore('color').$;
  }

  private getShapeCount$() {
    return this.objectStore('childShapeIds').$.pipe(
      filter(isDefined),
      map((childShapeIds) => childShapeIds.length),
    );
  }

  private getSubObjectCount$() {
    return this.objectStore('childObjectIds').$.pipe(
      filter(isDefined),
      map((childObjectIds) => childObjectIds.length),
    );
  }

  private getMissingAttributesTooltip$() {
    const missingObjectAttribute$ = this.getMissingObjectAttributeError$();

    const nestedObjectIds$ = this.objectScopingService
      .getNestedObjectIds$(this.objectItem.index)
      .pipe(cache());

    const missingNestedObjectAttribute$ =
      this.getMissingNestedObjectAttributeError$(nestedObjectIds$);

    const missingShapeAttribute$ =
      this.getMissingShapeAttributeError$(nestedObjectIds$);

    const missingClass$ = this.getMissingClassError$();

    const missingNestedClass$ =
      this.getMissingNestedObjectClassError$(nestedObjectIds$);

    return combineLatest([
      missingObjectAttribute$,
      missingNestedObjectAttribute$,
      missingShapeAttribute$,
      missingClass$,
      missingNestedClass$,
    ]).pipe(
      map((missingAttributeLabels) =>
        missingAttributeLabels.filter(Boolean).join('\r\n'),
      ),
    );
  }

  private getMissingObjectAttributeError$() {
    return this.objectTransientInfoStore('hasMissingRequiredTags').$.pipe(
      map((hasMissingRequiredTag) =>
        hasMissingRequiredTag ? 'Missing object attributes' : null,
      ),
    );
  }

  private getMissingNestedObjectAttributeError$(
    nestedObjectIds$: Observable<number[]>,
  ) {
    return nestedObjectIds$.pipe(
      switchMap((nestedObjectIds) =>
        combineLatestOrEmpty(
          nestedObjectIds.map(
            (nestedObjectId) =>
              this.getObjectTransientInfoStore(nestedObjectId)(
                'hasMissingRequiredTags',
              ).$,
          ),
        ),
      ),
      map((hasMissingRequiredTag) =>
        some(hasMissingRequiredTag) ? 'Missing nested object attributes' : null,
      ),
    );
  }

  private getMissingShapeAttributeError$(
    nestedObjectIds$: Observable<number[]>,
  ) {
    const shapeIds$ = this.getNestedShapeIds$(nestedObjectIds$);

    return shapeIds$.pipe(
      switchMap((shapeIds) =>
        combineLatestOrEmpty(this.getShapesTransientInfo$(shapeIds)),
      ),
      map((areShapeMissingRequiredTag) =>
        some(areShapeMissingRequiredTag) ? 'Missing shape attributes' : null,
      ),
    );
  }

  private getMissingClassError$() {
    return this.objectTransientInfoStore('hasMissingClass').$.pipe(
      map((hasMissingClass) =>
        hasMissingClass ? 'Missing object class' : null,
      ),
    );
  }

  private getMissingNestedObjectClassError$(
    nestedObjectIds$: Observable<number[]>,
  ) {
    return nestedObjectIds$.pipe(
      switchMap((nestedObjectIds) =>
        combineLatestOrEmpty(
          nestedObjectIds.map((nestedObjectId) => {
            const nestedObjectTransientInfo =
              this.getObjectTransientInfoStore(nestedObjectId);
            return nestedObjectTransientInfo('hasMissingClass').$;
          }),
        ),
      ),

      map((hasMissingClass) =>
        some(hasMissingClass) ? 'Missing nested object class' : null,
      ),
    );
  }

  private getShapesTransientInfo$(shapeIds: number[]) {
    return shapeIds.map((shapeId) => {
      const shapeTransientInfoStore =
        this.taskWorkspaceStore('shapeTransientInfo')(shapeId);
      // when deleting shapes, transient info might not exist
      if (isStoreDefined(shapeTransientInfoStore)) {
        return shapeTransientInfoStore('hasMissingRequiredTags').$;
      }
      return EMPTY;
    });
  }

  private getNestedShapeIds$(nestedObjectIds$: Observable<number[]>) {
    return combineLatest([
      this.objectStore('childShapeIds').$,
      nestedObjectIds$.pipe(
        switchMap((nestedObjectIds) =>
          combineLatestOrEmpty(
            nestedObjectIds.map((nestedObjectId) => {
              const nestedObjectStore =
                this.taskWorkspaceStore.getObjectStore(nestedObjectId);
              return nestedObjectStore('childShapeIds').$;
            }),
          ),
        ),
      ),
    ]).pipe(map(flattenDeep));
  }

  private setUpScrollIntoViewSubscription() {
    this.subscribeTo(this.selected$, (selected) => {
      const selection = this.taskWorkspaceStore.state().objectSelection;
      if (selected && size(selection) === 1) {
        (this.elementRef.nativeElement as Element).scrollIntoView({
          block: 'nearest',
        });
      }
    });
  }

  private getObjectTransientInfoStore(objectId: number) {
    const store = this.taskWorkspaceStore('objectTransientInfo')(objectId);
    assertStore(store, `Object ${objectId} has no transient info`);
    return store;
  }
}
